import { WidgetSubtype, IWidget, WidgetType } from './Widget';
import { getWidgetTypeOption } from 'client/components/schema/widget/setup/helper/getWidgetTypeListOptions';
import { MetadataLogic } from '../metadata/MetadataLogic';
import * as uuid from 'uuid';
import { IProcedure } from '../Procedure';
import { WidgetInputType } from './input/WidgetInputType';
import {
  DynamicFormulaNode,
  WidgetDynamicViewType
} from './dynamic/WidgetDynamic';
import { WidgetInputConditional } from './input/WidgetInputConditional';
import { assertNever } from 'server/utils/typings/assertNever';
import { WidgetGraphicType } from './graphic/WidgetGraphic';
import { WidgetInputFileTypes } from './input/WidgetUploadFileType';
import { IWidgetInput } from './input/WidgetInput';

export class WidgetLogic {
  /**
   * Crea un Widget come placeholder
   */
  static createPlaceholder(procedure: IProcedure): IWidget {
    const count = procedure.widgets.length + 1;

    return {
      id: uuid.v4(),
      isPlaceholder: true,
      type: WidgetType.Input,
      procedureId: procedure.id,
      justCreated: true,
      input: {
        id: uuid.v4(),
        type: WidgetInputType.Text
      }
    } as any;
  }

  /**
   * Crea un NestedWidget come placeholder
   */
  static createNestedPlaceholder(): IWidget {
    return {
      id: uuid.v4(),
      isPlaceholder: true,
      type: WidgetType.Input,
      justCreated: true,
      input: {
        id: uuid.v4(),
        type: WidgetInputType.Text
      }
    } as any;
  }

  /**
   * Aggiorna il widget configurando in base al subtype selezionato
   */
  static confirmSubtype(widget: IWidget, subtype: WidgetSubtype) {
    if (subtype == null) return widget;

    const option = getWidgetTypeOption(subtype);
    widget.type = option.type;

    // Elimino il placeholder
    delete widget.input;

    switch (option.type) {
      case WidgetType.Input:
        widget.input = {
          name: '',
          label: '',
          validation: {},
          conditional: {},
          options: [],
          type: option.subtype
        } as any;

        switch (option.subtype) {
          // Gli elementi di Upload del documento firmato sono richiesti a
          // priori e accettano un solo documento PDF.
          case WidgetInputType.UploadSigned: {
            widget.input!.required = true;
            widget.input!.validation.filetype = WidgetInputFileTypes.pdf;
            widget.input!.validation.multiple = false;
          }
        }

        break;
      case WidgetType.Graphic:
        widget.graphic = {
          type: option.subtype,
          conditional: {}
        } as any;
        break;
      case WidgetType.Dynamic:
        widget.dynamic = {
          type: option.subtype,
          name: '',
          label: '',
          view: WidgetDynamicViewType.None
        } as any;
        break;
    }

    MetadataLogic.createOnWidget(widget);
    widget.justCreated = false;
    return widget;
  }

  /**
   * Ottiene la Label del Widget
   * (da mostrare nell'elenco dei Widget Slave)
   */
  static getWidgetLabel(widget: IWidget): string {
    switch (widget.type) {
      case WidgetType.Input:
        return widget.input?.label ?? '';

      case WidgetType.Dynamic:
        return widget.dynamic?.label ?? '';

      case WidgetType.Graphic:
        const displayOrder = widget.order + 1;

        switch (widget.graphic!.type) {
          case WidgetGraphicType.Text:
            return `Widget Testo Libero (Posizione ${displayOrder})`;

          case WidgetGraphicType.Section:
            return `Widget Titolo (Posizione ${displayOrder})`;

          case WidgetGraphicType.Info:
            return `Widget Nota Informativa (Posizione ${displayOrder})`;

          case WidgetGraphicType.Divider:
            return `Widget Divisore Sezione (Posizione ${displayOrder})`;

          default:
            assertNever(widget.graphic!.type);
        }

      default:
        assertNever(widget.type);
    }
  }

  /** Ottiene il Nome del Widget */
  static getWidgetName(widget: IWidget): string {
    switch (widget.type) {
      case WidgetType.Input:
        return widget.input?.name ?? '';

      case WidgetType.Dynamic:
        return widget.dynamic?.name ?? '';

      case WidgetType.Graphic:
        const displayOrder = widget.order + 1;

        switch (widget.graphic!.type) {
          case WidgetGraphicType.Text:
            return `Widget Testo Libero (Posizione ${displayOrder})`;

          case WidgetGraphicType.Section:
            return `Widget Titolo (Posizione ${displayOrder})`;

          case WidgetGraphicType.Info:
            return `Widget Nota Informativa (Posizione ${displayOrder})`;

          case WidgetGraphicType.Divider:
            return `Widget Divisore Sezione (Posizione ${displayOrder})`;

          default:
            assertNever(widget.graphic!.type);
        }

      default:
        assertNever(widget.type);
    }
  }

  /**
   * Ottiene le condizioni da valutare di un widget per la visualizzazione condizionale
   */
  static getCondition(widget: IWidget): WidgetInputConditional {
    switch (widget.type) {
      case WidgetType.Input:
        return widget.input?.conditional ?? {};
      case WidgetType.Dynamic:
        return {};
      case WidgetType.Graphic:
        return widget.graphic?.conditional ?? {};

      default:
        assertNever(widget.type);
    }
  }

  /**
   * Controlla se un Widget ha una condizione
   */
  static hasCondition(widget: IWidget) {
    const condition = this.getCondition(widget);
    return condition.metadataId != null;
  }
}
